import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import PageShell from "@/components/PageShell";
import NextRoom from "@/components/NextRoom";
import RelatedGraph from "@/components/RelatedGraph";
import {
  OBSERVATIONS, DOMAINS,
  buildingForObservation, artifactsForObservation, relatedFieldNotesForObservation,
} from "@/data/content";

export default function ThinkingDetail() {
  const { slug } = useParams();
  const obs = OBSERVATIONS.find((o) => o.slug === slug);
  if (!obs) return <Navigate to="/thinking" replace />;

  const d = DOMAINS.find((x) => x.slug === obs.domain);
  const related = OBSERVATIONS.filter((o) => o.domain === obs.domain && o.slug !== obs.slug).slice(0, 3);
  const idx = OBSERVATIONS.findIndex((o) => o.slug === obs.slug);
  const prev = OBSERVATIONS[(idx - 1 + OBSERVATIONS.length) % OBSERVATIONS.length];
  const next = OBSERVATIONS[(idx + 1) % OBSERVATIONS.length];

  const relatedBuilding = buildingForObservation(obs.slug);
  const relatedArtifacts = artifactsForObservation(obs.slug);
  const relatedFieldNotes = relatedFieldNotesForObservation(obs.slug);

  return (
    <PageShell
      testId="page-thinking-detail"
      title={`${obs.headline} | Thinking | Quentin Young`}
      description={`${obs.body} An observation by Quentin Young in the ${d?.name} domain.`}
      ogImage="/og-thinking.jpg"
      breadcrumbs={[
        { name: "Home", url: "/" },
        { name: "Thinking", url: "/thinking" },
        { name: d?.name || "Observation", url: `/thinking?domain=${obs.domain}` },
        { name: obs.headline, url: `/thinking/${obs.slug}` },
      ]}
      article={{
        headline: obs.headline,
        description: obs.body,
      }}
    >
      <article className="container-narrow pt-16 md:pt-28 pb-24">
        <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-paper-mute mb-6">
          <Link to="/thinking" className="link-quiet">Thinking</Link> · {d?.name} · Cornerstone
        </p>
        <h1 className="display-serif text-4xl md:text-6xl lg:text-7xl text-paper-primary text-balance leading-[1.02]">
          {obs.headline}
        </h1>
        <p className="mt-10 font-serif text-xl md:text-2xl text-paper-secondary leading-relaxed text-pretty">
          {obs.body}
        </p>
        {obs.note && (
          <blockquote className="mt-12 border-l-2 border-gold/60 pl-6 font-serif italic text-lg text-paper-tertiary leading-relaxed">
            {obs.note}
          </blockquote>
        )}
      </article>

      {/* Related */}
      {related.length > 0 && (
        <section className="border-t border-ink-line bg-ink-surface py-16">
          <div className="container-edge">
            <p className="eyebrow mb-8">Related in {d?.name}</p>
            <ul className="grid grid-cols-1 md:grid-cols-3 gap-px bg-ink-line">
              {related.map((r) => (
                <li key={r.slug} className="bg-ink-surface">
                  <Link to={`/thinking/${r.slug}`} className="block p-8 group" data-testid={`related-${r.slug}`}>
                    <h3 className="font-serif text-xl text-paper-primary group-hover:text-gold-warm transition-colors duration-300 text-balance leading-snug">
                      {r.headline}
                    </h3>
                    <p className="mt-3 text-paper-tertiary text-sm">{r.body}</p>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      {/* Pager */}
      <section className="border-t border-ink-line bg-ink-base">
        <div className="container-edge grid grid-cols-2 gap-px bg-ink-line">
          <Link to={`/thinking/${prev.slug}`} className="bg-ink-base p-8 md:p-10 group" data-testid="thinking-prev">
            <p className="eyebrow mb-3">← Previous line of thought</p>
            <p className="font-serif text-lg md:text-xl text-paper-primary group-hover:text-gold-warm transition-colors text-balance">{prev.headline}</p>
          </Link>
          <Link to={`/thinking/${next.slug}`} className="bg-ink-base p-8 md:p-10 group text-right" data-testid="thinking-next">
            <p className="eyebrow mb-3">Continue the line of thought →</p>
            <p className="font-serif text-lg md:text-xl text-paper-primary group-hover:text-gold-warm transition-colors text-balance">{next.headline}</p>
          </Link>
        </div>
      </section>

      <RelatedGraph
        observations={[]}
        building={(obs.relatedBuilding || []).slice(0, 4)}
        artifacts={(obs.relatedArtifacts || []).slice(0, 4)}
        pillar={obs.pillar || null}
        fieldNotes={relatedFieldNotes.slice(0, 4)}
        eyebrow="Follow the thread"
        title="Where this idea leaves fingerprints."
      />

      <NextRoom
        eyebrow="Move to the next room"
        title="Ideas live outside pages."
        rooms={[
          relatedBuilding[0]
            ? { label: `See this idea in practice: ${relatedBuilding[0].name}`, to: `/building/${relatedBuilding[0].slug}`, hint: "proof" }
            : { label: "See the work this thinking produced", to: "/building", hint: "proof" },
          { label: "Bring this thinking into a room", to: "/speaking", hint: "speaking" },
          { label: "Read the next observation", to: `/thinking/${next.slug}`, hint: "next line of thought" },
        ]}
        testId="thinking-detail-next"
      />
    </PageShell>
  );
}
